/* eslint-disable no-param-reassign */
import type { DrawBounds } from "./draw-bounds.ts";
import { bitmapChannels } from "../bitmap/types.ts";
import { bitmapToImageData } from "../bitmap/image-data.ts";
import { pixAPicKit } from "../pix-a-pic.ts";
import type { CanvasWithSourceDimensions } from "./canvas-types.ts";
import type { BuildInput } from "./build-input.ts";
import { normaliseBuildInput } from "./build-input.ts";

function* renderPictureAsPixels(
	ctx: CanvasImageSmoothing &
		CanvasDrawImage &
		CanvasImageData &
		CanvasWithSourceDimensions,
	utilityCtx: CanvasImageData & {
		readonly canvas: CanvasImageSource & {
			readonly width: number;
			readonly height: number;
		};
	},
	buildInput: BuildInput,
	explicitDrawBounds?: DrawBounds,
) {
	const { noBrickColour, ...image } = normaliseBuildInput(buildInput);
	if (
		utilityCtx.canvas.width < image.width ||
		utilityCtx.canvas.height < image.height
	) {
		throw new Error(
			`Cache canvas (${utilityCtx.canvas.width}x${utilityCtx.canvas.height}) 
			must be >= image (${image.width}x${image.height}) for pixel render`,
		);
	}

	const drawBounds = explicitDrawBounds ?? {
		x: 0,
		y: 0,
		width: ctx.canvas.width,
		height: ctx.canvas.height,
	};

	const smallData = bitmapToImageData(image);
	if (noBrickColour !== undefined) {
		// rgba is packed 0xRRGGBBAA. Compare only the RGB channels (alpha is always
		// opaque here) as a single 24-bit value rather than three byte comparisons.
		const noBrickRgb = (noBrickColour.rgba >>> 8) & 0xffffff;
		const {
			basePlateColour: { r: basePlateR, g: basePlateG, b: basePlateB },
		} = pixAPicKit;
		const { data } = smallData;
		for (let i = 0; i < data.length; i += bitmapChannels) {
			const pixelRgb = (data[i] << 16) | (data[i + 1] << 8) | data[i + 2];
			if (pixelRgb === noBrickRgb) {
				data[i] = basePlateR;
				data[i + 1] = basePlateG;
				data[i + 2] = basePlateB;
			}
		}
	}
	utilityCtx.putImageData(smallData, 0, 0);
	yield;
	const previousImageSmoothing = ctx.imageSmoothingEnabled;
	ctx.imageSmoothingEnabled = false;
	ctx.drawImage(
		utilityCtx.canvas,
		0,
		0,
		smallData.width,
		smallData.height,
		drawBounds.x,
		drawBounds.y,
		drawBounds.width,
		drawBounds.height,
	);
	ctx.imageSmoothingEnabled = previousImageSmoothing;

	// TODO: Currently not working nicely. Need to snap colours
	// during resize - "nearest neighbour". Atm it's blurring
	// const resized = yield* resizeBitmap(
	// 	image,
	// 	drawBounds.width,
	// 	drawBounds.height,
	// );
	// const resized = yield* resizeBitmapNearestNeighbour(
	// 	image,
	// 	drawBounds.width,
	// 	drawBounds.height,
	// );
	// ctx.putImageData(bitmapToImageData(resized), drawBounds.x, drawBounds.y);

	// Requires async, also not sure about node compat. Note: haven't tried it yet
	// ctx.drawImage(
	// 	createImageBitmap(bitmapToImageData(image)),
	// 	drawBounds.x,
	// 	drawBounds.y,
	// 	drawBounds.width,
	// 	drawBounds.height,
	// )

	// We used to use canvas to scale down.
	// Canvas almost definitely more efficient than resizeBitmap function,
	// but means this API relies on canvas, which doesn't work for workers.
	// ctx.imageSmoothingEnabled = false;
	// const smallCanvas = withTemporaryCanvasContext2d(
	// 	image.width,
	// 	image.height,
	// 	(tempCtx) => {
	// 		const smallData = bitmapToImageData(image);
	// 		tempCtx.putImageData(smallData, 0, 0);
	// 		return tempCtx.canvas;
	// 	},
	// );
	// ctx.drawImage(
	// 	smallCanvas,
	// 	drawBounds.x,
	// 	drawBounds.y,
	// 	drawBounds.width,
	// 	drawBounds.height,
	// );
	// ctx.imageSmoothingEnabled = true;
}

export default renderPictureAsPixels;
